import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { billingApi, type Entitlement } from '../lib/reportsApi';
import { PageHeader, Button, Badge } from '../components/ui/light';

const PLANS = [
  {
    id: 'free' as const,
    name: 'Free',
    price: 0,
    tagline: 'Try the report experience',
    features: [
      '5 free reports lifetime',
      'Audience intelligence',
      'Cohort income bands',
      'Basic monetization overview',
    ],
    cta: 'Current plan',
  },
  {
    id: 'pro' as const,
    name: 'Pro',
    price: 20,
    tagline: 'Unlock the full playbook',
    badge: 'Most popular',
    features: [
      'Unlimited premium reports',
      'Product matching with score breakdown',
      'Vendor matches with commission ranges',
      'Posting timing intelligence',
      'Full monetization breakdown',
      '6+ priority actions',
    ],
    cta: 'Upgrade to Pro',
  },
  {
    id: 'scale' as const,
    name: 'Scale',
    price: 40,
    tagline: 'For full-time creators',
    features: [
      'Everything in Pro',
      'Brand-deal pipeline',
      'Estimated value bands per brand',
      'Priority opportunities',
      'Quarterly performance review',
    ],
    cta: 'Upgrade to Scale',
  },
];

export default function Upgrade() {
  const navigate = useNavigate();
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [loadingPlan, setLoadingPlan] = useState<'pro' | 'scale' | null>(null);

  useEffect(() => {
    billingApi.me()
      .then((r) => setEntitlement(r.data.entitlement))
      .catch(() => { /* silent */ });
  }, []);

  const handleUpgrade = async (plan: 'pro' | 'scale') => {
    setLoadingPlan(plan);
    try {
      const res = await billingApi.checkout(plan);
      if (res.data.dev) {
        // Dev mode: subscription was activated server-side; bounce back
        window.location.href = '/dashboard';
      } else {
        window.location.href = res.data.redirect_url;
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Checkout failed');
      setLoadingPlan(null);
    }
  };

  return (
    <div className="theme-light l-page">
      <div className="l-shell py-10 max-w-6xl">
        <PageHeader
          eyebrow="Upgrade"
          title="Choose your plan"
          subtitle="Premium reports unlock product matches, vendor connections, brand deals, and the full action plan."
          back={{ label: 'Back', onClick: () => navigate(-1) }}
        />

        <div className="grid md:grid-cols-3 gap-5">
          {PLANS.map((p) => {
            const isCurrent = entitlement?.plan === p.id;
            const showCta = !isCurrent && p.id !== 'free';
            return (
              <div
                key={p.id}
                className="p-7 flex flex-col"
                style={{
                  background: p.id === 'pro' ? 'var(--ch2-ink)' : 'var(--ch2-surface)',
                  color: p.id === 'pro' ? '#ffffff' : 'var(--ch2-ink)',
                  border: '1px solid var(--ch2-border)',
                  borderRadius: 'var(--ch2-radius-lg)',
                  boxShadow: p.id === 'pro' ? 'var(--ch2-shadow-lg)' : 'var(--ch2-shadow-sm)',
                  position: 'relative',
                  transform: p.id === 'pro' ? 'translateY(-6px)' : undefined,
                }}
              >
                {p.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge variant="accent">
                      <Sparkles className="w-3 h-3 mr-1 inline" />
                      {p.badge}
                    </Badge>
                  </div>
                )}
                <div className="text-sm uppercase tracking-wider opacity-60 mb-2">{p.name}</div>
                <div className="font-display font-bold mb-1" style={{ fontSize: 40, lineHeight: 1 }}>
                  ${p.price}
                  <span className="text-base font-medium opacity-50 ml-1">/mo</span>
                </div>
                <div className="text-sm opacity-70 mb-5">{p.tagline}</div>
                <ul className="space-y-2 mb-6 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: p.id === 'pro' ? '#FFD400' : 'var(--ch2-success)' }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <Button variant="ghost" disabled>Current plan</Button>
                ) : showCta ? (
                  <Button
                    variant={p.id === 'pro' ? 'accent' : 'primary'}
                    onClick={() => handleUpgrade(p.id as 'pro' | 'scale')}
                    loading={loadingPlan === p.id}
                  >
                    {p.cta}
                  </Button>
                ) : (
                  <Button variant="secondary" onClick={() => navigate('/reports')}>Get started</Button>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center l-subtle text-sm mt-10">
          Cancel anytime · 30-day full refund · Powered by Shopify
        </p>
      </div>
    </div>
  );
}
